import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import {
    BaseSpellContract,
    characterActions,
    CharClass,
    ClassSpellInfo,
    ClassUtils,
    DataOriginRefData,
    EntityValueLookup,
    OverallSpellInfo,
    RuleData,
    rulesEngineSelectors,
    Spell,
    SpellCasterInfo,
    serviceDataActions,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';

import SpellSlotManager from '../../../../CharacterSheet/containers/SpellSlotManager';
import ClassSpellManager from '../../../components/ClassSpellManager';
import * as apiCreatorSelectors from '../../../selectors/composite/apiCreator';
import { MakeClassBasedApiSpellsRequest } from '../../../selectors/composite/apiCreator';
import { SharedAppState } from '../../../stores/typings';
import { AppNotificationUtils } from '../../../utils';

interface Props extends DispatchProp {
    availableSpellSlotLevels: Array<number>;
    classSpellLists: Array<ClassSpellInfo>;
    spellCasterInfo: SpellCasterInfo;
    ruleData: RuleData;
    makeLoadClassAlwaysKnownSpells: MakeClassBasedApiSpellsRequest;
    makeLoadClassRemainingSpells: MakeClassBasedApiSpellsRequest;
    entityValueLookup: EntityValueLookup;
    overallSpellInfo: OverallSpellInfo;
    dataOriginRefData: DataOriginRefData;
    proficiencyBonus: number;
    theme: CharacterTheme;
}
class SpellManagePane extends React.PureComponent<Props> {
    handleSpellAdd = (charClass: CharClass, spell: Spell, classMappingId: number): void => {
        const { dispatch } = this.props;
        dispatch(
            characterActions.spellCreate(
                spell,
                classMappingId,
                AppNotificationUtils.handleSpellCreateAccepted.bind(this, spell, charClass),
            ),
        );
    };

    handlePrepare = (spell: Spell, classMappingId: number): void => {
        const { dispatch } = this.props;
        dispatch(characterActions.spellPreparedSet(spell, classMappingId, true));
    };

    handleUnprepare = (spell: Spell, characterClassId: number): void => {
        const { dispatch } = this.props;
        dispatch(characterActions.spellPreparedSet(spell, characterClassId, false));
    };

    handleRemove = (spell: Spell, characterClassId: number): void => {
        const { dispatch } = this.props;
        dispatch(characterActions.spellRemove(spell, characterClassId));
    };

    handleAlwaysKnownLoad = (spells: Array<BaseSpellContract>, classId: number): void => {
        const { dispatch } = this.props;
        dispatch(serviceDataActions.classAlwaysKnownSpellsSet(spells, classId));
    };

    renderDefault = () => {
        return (
            <div className="ct-spell-manage-pane__default">
                You do not have spellcasting or pact magic spells to manage. The spells that appear for your character
                were gained through other class features, racial traits, feats, or items.
            </div>
        );
    };

    render() {
        const {
            classSpellLists,
            spellCasterInfo,
            ruleData,
            makeLoadClassRemainingSpells,
            makeLoadClassAlwaysKnownSpells,
            entityValueLookup,
            overallSpellInfo,
            dataOriginRefData,
            proficiencyBonus,
            theme,
        } = this.props;

        if (!classSpellLists.length) {
            return this.renderDefault();
        }

        return (
            <div className="ct-spell-manage-pane">
                <SpellSlotManager />
                {classSpellLists.map((classSpellList) => (
                    <ClassSpellManager
                        {...classSpellList}
                        key={ClassUtils.getMappingId(classSpellList.charClass)}
                        hasMultipleSpellClasses={classSpellLists.length > 1}
                        loadRemainingSpellList={makeLoadClassRemainingSpells(classSpellList.charClass)}
                        loadAlwaysKnownSpells={makeLoadClassAlwaysKnownSpells(classSpellList.charClass)}
                        onAlwaysKnownLoad={this.handleAlwaysKnownLoad}
                        onSpellPrepare={this.handlePrepare}
                        onSpellUnprepare={this.handleUnprepare}
                        onSpellRemove={this.handleRemove}
                        onSpellAdd={this.handleSpellAdd.bind(this, classSpellList.charClass)}
                        spellCasterInfo={spellCasterInfo}
                        ruleData={ruleData}
                        entityValueLookup={entityValueLookup}
                        overallSpellInfo={overallSpellInfo}
                        dataOriginRefData={dataOriginRefData}
                        isPrepareMaxed={!!classSpellList.isPrepareMaxed}
                        proficiencyBonus={proficiencyBonus}
                        theme={theme}
                    />
                ))}
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        availableSpellSlotLevels: rulesEngineSelectors.getAvailableSpellSlotLevels(state),
        classSpellLists: rulesEngineSelectors.getClassSpellLists(state),
        spellCasterInfo: rulesEngineSelectors.getSpellCasterInfo(state),
        overallSpellInfo: rulesEngineSelectors.getOverallSpellInfo(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        makeLoadClassRemainingSpells: apiCreatorSelectors.makeLoadClassRemainingSpells(state),
        makeLoadClassAlwaysKnownSpells: apiCreatorSelectors.makeLoadClassAlwaysKnownSpells(state),
        entityValueLookup: rulesEngineSelectors.getCharacterValueLookupByEntity(state),
        dataOriginRefData: rulesEngineSelectors.getDataOriginRefData(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
    };
}

export default connect(mapStateToProps)(SpellManagePane);
